import {
  ALLOWED_DOCUMENT_TYPES,
  ALLOWED_MEDIA_TYPES,
  ALLOWED_VIDEO_TYPES,
  MediaContentType,
  MediaKind,
} from "../../interfaces/MediaObject";

// Extension → content type fallback. Some browsers report an empty `file.type`
// (notably .mov on certain platforms), so we recover the type from the name.
const EXTENSION_CONTENT_TYPES: Record<string, MediaContentType> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  pdf: "application/pdf",
};

const mediaUtility = {
  /** Whether a content type is one the storage API accepts. */
  isAllowedContentType(contentType: string): contentType is MediaContentType {
    return (ALLOWED_MEDIA_TYPES as readonly string[]).includes(contentType);
  },

  /**
   * Turns a base64 string (no data: prefix) into a File the presigned-upload
   * pipeline (useMediaUpload/StorageApiService) can take — e.g. the inline
   * base64 image the AI image-revamp endpoint returns
   * (POST /api/v1/imagegen/image-revamp), which is never itself stored
   * server-side and must be uploaded like any other picked file if it's kept.
   * atob/File are browser-only — this is not usable from Node/SSR code.
   */
  base64ToFile(base64: string, fileName: string, contentType: MediaContentType): File {
    const byteChars = atob(base64);
    // Write straight into the typed array — a plain Array of the same length
    // first (then re-copied into Uint8Array) briefly doubles peak memory for
    // no reason on a multi-MB AI-generated photo.
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      bytes[i] = byteChars.charCodeAt(i);
    }
    return new File([bytes], fileName, { type: contentType });
  },

  /**
   * Resolves a file's content type. Prefers the browser-reported `file.type`,
   * then falls back to the file extension. Returns null if the type isn't
   * supported — callers should surface a "type not supported" error.
   */
  resolveContentType(file: File): MediaContentType | null {
    if (this.isAllowedContentType(file.type)) return file.type;
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    const guessed = EXTENSION_CONTENT_TYPES[ext];
    return guessed ?? null;
  },

  /**
   * Resolves a content type from a bare file name or object path (by extension).
   * Returns null for unknown extensions. Used when normalizing backend records
   * that don't carry an explicit mediaType.
   */
  contentTypeFromName(nameOrPath: string): MediaContentType | null {
    const ext = nameOrPath.split("?")[0].split(".").pop()?.toLowerCase() ?? "";
    return EXTENSION_CONTENT_TYPES[ext] ?? null;
  },

  /** Maps a content type to the coarse UI category used to pick a renderer. */
  kindFromContentType(contentType: string): MediaKind {
    if ((ALLOWED_VIDEO_TYPES as readonly string[]).includes(contentType)) return "video";
    if ((ALLOWED_DOCUMENT_TYPES as readonly string[]).includes(contentType)) return "pdf";
    // Default to image — covers every ALLOWED_IMAGE_TYPES entry.
    return "image";
  },

  /**
   * Value for an <input type="file"> accept attribute covering photos & videos.
   *
   * DELIBERATELY excludes application/pdf — see documentAcceptAttribute()
   * below for why. Uses wildcard MIME groups (image/*, video/*) rather than
   * exact subtypes because Safari on macOS/iOS doesn't reliably honor exact
   * video subtypes (video/mp4, video/quicktime, video/webm) either. Either
   * way this is a UI hint only; resolveContentType() above remains the real
   * gatekeeper, so anything that slips through an over-broad accept is still
   * rejected after selection with a "type not supported" error.
   */
  mediaAcceptAttribute(): string {
    return "image/*,video/*";
  },

  /**
   * Value for an <input type="file"> accept attribute covering PDFs only.
   *
   * Kept as a SEPARATE file input from mediaAcceptAttribute() — never merge
   * "application/pdf" into the same accept list as image/video types. WebKit
   * (Safari on macOS/iOS) only offers its native Photos-gallery quick actions
   * ("Photo Library" / "Take Photo or Video") when every token in `accept` is
   * a photo/video type. The moment a non-media type like application/pdf is
   * mixed in, Safari drops the Photos-gallery UI entirely and falls back to
   * a plain Files browser — which is why videos (and photos) appeared to
   * vanish from "the gallery" picker on Mac/iOS even after switching to
   * wildcard video types. Keeping PDFs on their own input/trigger preserves
   * the native gallery for the common photo/video case.
   */
  documentAcceptAttribute(): string {
    return "application/pdf";
  },

  /** Human label for a kind — used in tiles, alt text and error copy. */
  labelForKind(kind: MediaKind): string {
    if (kind === "video") return "Video";
    if (kind === "pdf") return "PDF";
    return "Photo";
  },

  /** Derives a display file name from an object path (last path segment). */
  fileNameFromPath(objectPath: string): string {
    if (!objectPath) return "file";
    const segment = objectPath.split("/").pop() ?? objectPath;
    return decodeURIComponent(segment);
  },

  /**
   * Whether a string is an absolute http(s) URL rather than a bucket-relative
   * storage path. Single source of truth for this check — used both when
   * rebuilding a publicUrl from an objectPath (mediaConvertor) and when
   * telling a representative-catalog media item apart from an own upload
   * (ProductDraftMediaHelper.isRepresentativeMedia), since catalog picks are
   * persisted with their full URL AS the objectPath.
   */
  isAbsoluteUrl(value: string): boolean {
    return /^https?:\/\//i.test(value);
  },

  /**
   * Safe value for a CSS `background-image: url(...)` declaration.
   *
   * NEVER interpolate a media URL into an unquoted `url(${value})` template —
   * unlike an <img src>/<a href> attribute (which the browser's URL parser
   * happily percent-encodes on the fly, spaces included), CSS's "consume a
   * url token" grammar rejects any unescaped whitespace inside an UNQUOTED
   * url() and drops the whole declaration as invalid. Object paths carry
   * seller-entered file names verbatim (e.g. "tendeer co_<uuid>.jpg") so a raw
   * space is routine, not an edge case — this is exactly why "the image
   * won't load" bugs kept showing up only on the CSS-background code paths
   * (MobileGallery, hover-zoom lenses, business gallery covers) and never on
   * the plain <img> ones. Wrapping in quotes fixes it: a quoted url("...")
   * token allows any character except an unescaped quote/backslash/newline.
   *
   * Returns the full `url("...")` string, ready to drop straight into a
   * `backgroundImage` style value. Pass "" / null / undefined through as
   * `url("")` (paints nothing, same as an empty background-image).
   */
  cssUrl(value: string | null | undefined): string {
    const escaped = (value ?? "").replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    return `url("${escaped}")`;
  },
} as const;

export default mediaUtility;
